<template>
  <div class="bc-card">
    <div class="bc-header">
      <div class="bc-header-icon">
        <i class="fal fa-chart-bar"></i>
      </div>
      <div>
        <h3 class="bc-title">{{ titleChart }}</h3>
        <p v-if="headingChart" class="bc-subtitle">{{ headingChart }}</p>
      </div>
    </div>
    <div class="bc-body">
      <canvas :id="chartId"></canvas>
    </div>
  </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { Chart } from 'chart.js'

export default {
  name: 'BarChart',
  props: {
    chartId: String,
    titleChart: String,
    headingChart: String,
    chartData: Object,
  },
  mounted() {
    this.createChart(this.chartId, this.chartData)
  },
  methods: {
    createChart(chartId, chartData) {
      const ctx = document.getElementById(chartId)

      // Enhance bar charts
      if (chartData.type === 'bar' && chartData.data.datasets[0]) {
        chartData.data.datasets[0].borderRadius = 4
        chartData.data.datasets[0].borderSkipped = false
        chartData.data.datasets[0].barThickness = 14
        chartData.data.datasets[0].maxBarThickness = 20
      }

      // Enhanced options
      const enhancedOptions = {
        ...chartData.options,
        responsive: true,
        maintainAspectRatio: true,
        layout: {
          padding: { top: 20 }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: '#94a3b8',
              font: { size: 11, weight: '500' }
            }
          },
          y: {
            grid: {
              color: '#f1f5f9',
              drawBorder: false
            },
            ticks: {
              color: '#94a3b8',
              font: { size: 11 },
              padding: 8
            },
            beginAtZero: true
          }
        },
        plugins: {
          ...chartData.options.plugins,
          datalabels: {
            anchor: 'end',
            align: 'top',
            color: '#64748b',
            font: { size: 11, weight: '600' },
            formatter: (value) => value > 0 ? value : ''
          },
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: '#0f172a',
            titleColor: '#fff',
            bodyColor: '#fff',
            padding: 10,
            cornerRadius: 8,
            titleFont: { size: 12, weight: '600' },
            bodyFont: { size: 12 },
            displayColors: false
          }
        }
      }

      new Chart(ctx, {
        type: chartData.type,
        data: chartData.data,
        options: enhancedOptions,
      })
    },
  },
}
</script>

<style scoped>
.bc-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.bc-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    border-bottom: 1px solid #f1f5f9;
}

.bc-header-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: rgba(8, 145, 178, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.bc-header-icon i {
    color: #0891B2;
    font-size: 1rem;
}

.bc-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.bc-subtitle {
    font-size: 0.75rem;
    color: #94a3b8;
    margin: 2px 0 0;
}

.bc-body {
    padding: 20px;
}
</style>
